/**
 * OrgPendingQueue: the pending-approval queue, a Carbon DataTable above the
 * full organization list.
 *
 * Renders only when there ARE pending rows; the parent owns the empty case
 * (the queue's absence), which keeps the verdict in one place.
 *
 * Voice (CLAUDE.md Product Voice): each row leads with the named subject in
 * a complete sentence ("Acme Corp is waiting for review.") rather than a row
 * count, and the actions are verbs ("Approve" / "Reject"). Pending is a
 * decision, not a severity, so the rows carry no hue.
 */

import { useState } from 'react'

import type { OrganizationListItemDto } from '@/lib/api'
import { Button } from '@/components/carbon/Button'
import { DataTable, TableItem, type DataColumn } from '@/components/carbon/DataTable'

interface Props {
  rows: OrganizationListItemDto[]
  onApprove: (row: OrganizationListItemDto) => void | Promise<void>
  onReject: (row: OrganizationListItemDto) => void | Promise<void>
}

function formatSubmissionDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }).format(d)
}

export default function OrgPendingQueue({ rows, onApprove, onReject }: Props) {
  if (rows.length === 0) return null

  const columns: DataColumn<OrganizationListItemDto>[] = [
    {
      id: 'org',
      header: 'Organization',
      primary: true,
      cell: row => (
        <TableItem
          title={`${row.name} is waiting for review.`}
          sub={`${row.primaryContactEmail || row.primaryContactName || 'No contact provided'} · Submitted ${formatSubmissionDate(row.createdAt)}`}
        />
      ),
    },
    {
      id: 'actions',
      header: <span className="sr-only">Decision</span>,
      align: 'end',
      cell: row => <PendingActions row={row} onApprove={onApprove} onReject={onReject} />,
    },
  ]

  return (
    <DataTable
      title="Waiting for review"
      description="Approve an organization to let its people sign in, or reject the request."
      columns={columns}
      rows={rows}
      rowId={row => row.id}
      rowLabel={row => row.name}
    />
  )
}

function PendingActions({
  row,
  onApprove,
  onReject,
}: {
  row: OrganizationListItemDto
  onApprove: (row: OrganizationListItemDto) => void | Promise<void>
  onReject: (row: OrganizationListItemDto) => void | Promise<void>
}) {
  // Per-row busy flag, set on click and cleared in the action's `finally`.
  // Scoping is intentional: the hook's global `isPending` doesn't tell us
  // *which* row the admin clicked, so neighboring rows would otherwise all
  // show busy simultaneously while only one is actually mid-flight.
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null)

  const handle = async (action: 'approve' | 'reject') => {
    setBusy(action)
    try {
      if (action === 'approve') await onApprove(row)
      else await onReject(row)
    } finally {
      setBusy(null)
    }
  }

  return (
    <span className="inline-flex flex-wrap justify-end gap-2 max-c-md:justify-start">
      <Button
        kind="tertiary"
        size="sm"
        onClick={() => void handle('approve')}
        disabled={busy !== null}
        loading={busy === 'approve'}
        aria-label={`Approve ${row.name}`}
      >
        Approve
      </Button>
      <Button
        kind="ghost"
        size="sm"
        onClick={() => void handle('reject')}
        disabled={busy !== null}
        loading={busy === 'reject'}
        aria-label={`Reject ${row.name}`}
      >
        Reject
      </Button>
    </span>
  )
}
